// ═══════════════════════════════════════════════════════════
// RollCall — Meal Tracking
// Meal events with counts, filterable by event
// ═══════════════════════════════════════════════════════════

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconToolsKitchen2, IconUsers, IconCalendarEvent } from '@tabler/icons-react';
import { useRollCallStore } from '../../store/rollcall-store';
import { useAuthStore } from '../../auth/auth-store';
import { GabStatCard, GabBadge, GabDataTable } from '../../components/gab';
import { fmtDateTime } from '../../lib/date-format';

export default function MealTracking() {
  const { events, checkins, fetchEvents, fetchCheckins } = useRollCallStore();
  const activeIncident = useAuthStore(s => s.activeIncident);
  const navigate = useNavigate();

  useEffect(() => { fetchEvents(); fetchCheckins(); }, []);

  const mealEvents = useMemo(() => {
    return events
      .filter(e => e.eventType === 'meal')
      .filter(e => activeIncident ? String(e.incidentId) === String(activeIncident.id) : true)
      .sort((a, b) => (b.startTime || '').localeCompare(a.startTime || ''));
  }, [events, activeIncident]);

  const mealStats = useMemo(() => {
    let totalMeals = 0;
    const uniquePersonnel = new Set<string>();
    mealEvents.forEach(evt => {
      const evtCheckins = checkins.filter(c => String(c.eventId) === String(evt.id));
      totalMeals += evtCheckins.length;
      evtCheckins.forEach(c => uniquePersonnel.add(c.personnelId || c.personName));
    });
    return { totalMeals, uniquePersonnel: uniquePersonnel.size, eventCount: mealEvents.length };
  }, [mealEvents, checkins]);

  type Row = { id: number; eventName: string; startTime: string; location: string; eventStatus: string; mealCount: number; agencies: number } & Record<string, unknown>;

  const rows: Row[] = useMemo(() => mealEvents.map(evt => {
    const evtCheckins = checkins.filter(c => String(c.eventId) === String(evt.id));
    const agencies = new Set(evtCheckins.map(c => c.agency).filter(Boolean));
    return {
      id: evt.id,
      eventName: evt.eventName,
      startTime: evt.startTime,
      location: evt.location,
      eventStatus: evt.eventStatus,
      mealCount: evtCheckins.length,
      agencies: agencies.size,
    };
  }), [mealEvents, checkins]);

  const columns = [
    { key: 'eventName', label: 'Event', sortable: true, render: (r: Row) => <span className="fw-semibold">{r.eventName}</span> },
    { key: 'startTime', label: 'Date', width: 160, render: (r: Row) => fmtDateTime(r.startTime) },
    { key: 'location', label: 'Location' },
    { key: 'mealCount', label: 'Meals', width: 80, render: (r: Row) => <span className="fw-bold">{r.mealCount}</span> },
    { key: 'agencies', label: 'Agencies', width: 80 },
    { key: 'eventStatus', label: 'Status', width: 80, render: (r: Row) => <GabBadge variant={r.eventStatus === 'open' ? 'success' : 'neutral'}>{r.eventStatus}</GabBadge> },
  ];

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <GabStatCard label="Total Meals" value={mealStats.totalMeals} icon={<IconToolsKitchen2 size={24} />} iconColor="success" accentColor="var(--color-success)" />
        <GabStatCard label="Unique Personnel" value={mealStats.uniquePersonnel} icon={<IconUsers size={24} />} iconColor="primary" accentColor="var(--color-primary-700)" />
        <GabStatCard label="Meal Events" value={mealStats.eventCount} icon={<IconCalendarEvent size={24} />} iconColor="info" accentColor="var(--color-info)" />
      </div>

      <GabDataTable<Row> columns={columns} data={rows} rowKey={r => r.id} onRowClick={r => navigate(`/rollcall/event/${r.id}`)} striped emptyMessage="No meal events recorded. Create a meal-type event to start tracking." />
    </div>
  );
}
